import React from 'react';
import {
  AddOperandButton,
  Filter,
  getRootOperator,
  isIngressExp,
  OperatorType,
  shouldRenderWithFieldFilterBox,
} from '@fabric-ui/components';
import { emptyObject } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { isNullOrUndefined } from '@utils/utils';
import { useExpressionBuilder } from './ExpressionBuilderContext';
import { FieldFilterBoxBase } from './FieldFilterBoxBase';
import { useFilterConfig } from './FilterContext';
import { useExpressionOperations } from './hooks/useExpressionOperations';
import { LogicalOperatorType } from './types';

export type FieldOption = { value: string; disabled?: boolean; [key: string]: any };

interface RenderOperatorProps {
  filter: Filter;
  setFilter: any;
  rootOperator: LogicalOperatorType;
  level?: number;
  onToggleLogicalOperators: () => void;
}

export const hasEmptyValueOperand = (filter: Filter): boolean => {
  const operator = getRootOperator(filter);
  if (operator === 'and' || operator === 'or') {
    const operands = filter[operator]?.operands;
    return !!operands?.some(op => {
      const condition = op.expression?.stringCondition;
      return condition && Object.values(condition).some(v => v === '');
    });
  }
  return false;
};

export const hasMultipleInOperands = (filter: Filter): boolean => {
  const operator = getRootOperator(filter);
  if (operator !== 'and' && operator !== 'or') return false;
  const operands = filter[operator]?.operands;
  if (!operands || operands.length <= 1) return false;
  return operands.filter(op => op.expression?.stringCondition?.in).length > 1;
};

const RenderOperator: React.FC<RenderOperatorProps> = ({ filter, setFilter, rootOperator, level = 0, onToggleLogicalOperators }) => {
  const {
    defaultField,
    supportParser,
    canDeleteLast,
    relationsProps = emptyObject,
    fieldOnly = false,
    hasListTab = false,
    shouldShowIcon,
    icon,
  } = useExpressionBuilder();
  const { disabled, isVertical, size } = useFilterConfig();
  const { addInline, deleteAt, replaceAt } = useExpressionOperations(filter, setFilter, {
    canDeleteLast,
    defaultEmptyFilter: relationsProps.defaultEmptyFilter,
    defaultField,
    hasListTab,
    level,
    rootOperator,
    supportParser,
  });

  const operatorColors = {
    and: 'var(--color-surface-interactive-primary-default)',
    or: 'var(--color-border-status-warning-secondary)',
  };

  const operandsList = filter[rootOperator]?.operands;
  return operandsList?.map((innerFilter, index) => {
    const setInnerFilter = value => replaceAt(index, value);
    const onDelete = () => deleteAt(index);
    const addLogicalExpression = () => addInline(index);
    const isIngressExpression = isIngressExp(innerFilter);

    return (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={index} sx={{ borderLeft: `2px solid ${operatorColors[rootOperator]}`, ml: level * 2 }}>
        {index > 0 && operandsList.length > index && (
          <Box className={isVertical ? '-my-1 ml-[105px] flex items-start' : '-my-1 ml-[52.5px] flex items-start'}>
            <AddOperandButton
              style={{ translate: '-50%' }}
              size={size}
              operator={rootOperator}
              onClick={onToggleLogicalOperators}
              disabled={disabled}
            />
          </Box>
        )}
        {shouldRenderWithFieldFilterBox(innerFilter, hasListTab) &&
        !(hasListTab && hasEmptyValueOperand(innerFilter)) &&
        !(hasListTab && hasMultipleInOperands(innerFilter)) ? (
          <Box className="flex items-center justify-start gap-2">
            <FieldFilterBoxBase
              rootOperator={rootOperator as OperatorType}
              filter={innerFilter}
              setFilter={setInnerFilter}
              level={level}
              onDelete={onDelete}
              addLogicalExpression={fieldOnly || operandsList.length === 1 ? undefined : addLogicalExpression}
              isIngressExp={isIngressExpression}
            />
            {(isNullOrUndefined(shouldShowIcon) || shouldShowIcon?.(innerFilter)) && icon}
          </Box>
        ) : (
          <RenderOperator
            filter={innerFilter}
            setFilter={setInnerFilter}
            rootOperator={getRootOperator(innerFilter) as LogicalOperatorType}
            level={level + 1}
            onToggleLogicalOperators={onToggleLogicalOperators}
          />
        )}
      </Box>
    );
  });
};

export default RenderOperator;
